import { useSelector } from '@xstate/react';
import { Button, IconStartAdornment } from 'components/Button';
import { contactInfoMachine, ContactInfoState } from 'domains/signup/contactInfo/contactInfoMachine';
import { ContactInfoContext } from 'domains/signup/contactInfo/data/ContactInfoContext';
import { ContactInfoEnterEmail } from 'domains/signup/contactInfo/Steps/EnterEmail';
import { ContactInfoEnterName } from 'domains/signup/contactInfo/Steps/EnterName';
import { ContactInfoEnterPhone } from 'domains/signup/contactInfo/Steps/EnterPhone';
import { ContactInfoEnterState } from 'domains/signup/contactInfo/Steps/EnterState';
import { ContactInfoOfferLivePhoneCall } from 'domains/signup/contactInfo/Steps/OfferLivePhoneCall';
import { ContactInfoStateNotSupported } from 'domains/signup/contactInfo/Steps/StateNotSupported';
import { signupStateChangeListener } from 'domains/signup/data/mixpanelTracking';
import { SignupContext, withSignupMachineState } from 'domains/signup/signupFlow/context/signupContext';
import { useTranslation } from 'react-i18next';
import { useEffectOnce } from 'react-use';
import { useActorUtilities } from 'utils/xstate/context';
import { ActorRefFrom } from 'xstate';

const EnteringContactInfo: React.FC = () => {
  const contactInfoActor = SignupContext.useSelector(
    state => state.children.runContactInfoMachine as ActorRefFrom<typeof contactInfoMachine>,
  );

  // Subscribe to log state changes with mixpanel
  useEffectOnce(() => {
    return contactInfoActor.subscribe(({ value }) => {
      signupStateChangeListener(value as ContactInfoState);
    }).unsubscribe;
  });

  const hasBack = useSelector(contactInfoActor, state => state.can({ type: 'back' }));
  const { send } = contactInfoActor;

  const { t } = useTranslation();
  return (
    <ContactInfoContext.Provider value={useActorUtilities(contactInfoActor)}>
      {hasBack && (
        <Button
          variant="link"
          startAdornment={<IconStartAdornment name="AngleLeft" />}
          className="mb-24"
          onClick={() => send({ type: 'back' })}
        >
          {t('screener.backButton')}
        </Button>
      )}
      <ContactInfoEnterState />

      <ContactInfoStateNotSupported />

      <ContactInfoEnterPhone />
      <ContactInfoEnterEmail />
      <ContactInfoEnterName />
      <ContactInfoOfferLivePhoneCall />
    </ContactInfoContext.Provider>
  );
};

export const SignupEnteringContactInfo = withSignupMachineState(EnteringContactInfo, 'enteringContactInfo');
